// AppSearchBar - Search + tags row; sort/favorites/owned/pin live in the settings popup (desktop).
// URL query params are driven by AppSection (useSearchParams); this component stays controlled.

import React, { useState } from 'react';
import { DropdownSelect, RevoltPopup, RevoltSwitch } from '../../../components';
import './AppSearchBar.css';

function normalizeTag(str) {
  return str.trim().toLowerCase().replace(/\s+/g, '-');
}

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'lastModified', label: 'Modified' },
  { value: 'created', label: 'Created' },
];

const FAVORITES_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'favorites', label: 'Favorites only' },
  { value: 'nonFavorites', label: 'Non-favorites only' },
];

const OWNED_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'owned', label: 'Owned only' },
  { value: 'nonOwned', label: 'Non-owned only' },
];

export function AppSearchBar({
  searchTerm = '',
  onSearchChange,
  sortBy = 'lastModified',
  onSortChange,
  favoritesFilter = 'all',
  onFavoritesChange,
  ownedFilter = 'all',
  onOwnedChange,
  tags = [],
  onTagsChange,
  pinFavoritesTop = false,
  onPinFavoritesTopChange,
  isMobile = false,
  onOpenFilters,
}) {
  const [tagInput, setTagInput] = useState('');
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState({ x: 0, y: 0 });

  const handleAddTag = () => {
    const normalized = normalizeTag(tagInput);
    if (normalized && !tags.includes(normalized)) {
      onTagsChange?.([...tags, normalized]);
      setTagInput('');
    }
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    onTagsChange?.(tags.filter((t) => t !== tagToRemove));
  };

  const handleMoreSettingsClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (settingsMenuOpen) {
      setSettingsMenuOpen(false);
    } else {
      setSettingsAnchor({ x: e.clientX, y: e.clientY });
      setSettingsMenuOpen(true);
    }
  };

  // Mobile: search bar + filter button only; filters live in modal
  if (isMobile) {
    return (
      <div className="app-search-bar app-search-bar--mobile">
        <div className="app-search-bar__row app-search-bar__row--search">
          <div className="app-search-bar__search">
            <i className="fa-solid fa-magnifying-glass app-search-bar__search-icon" aria-hidden />
            <input
              type="text"
              placeholder="Search apps..."
              value={searchTerm}
              onChange={(e) => onSearchChange?.(e.target.value)}
              className="app-search-bar__input"
            />
          </div>
          <button
            type="button"
            className="app-search-bar__filter-btn"
            onClick={onOpenFilters}
            aria-label="Open filters"
            title="Filters"
          >
            <i className="fa-solid fa-sliders" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-search-bar">
      {/* Row 1: Search + settings (sliders icon; same as mobile — opens popup with sort / favorites / owned / pin) */}
      <div className="app-search-bar__row app-search-bar__row--search">
        <div className="app-search-bar__search">
          <i className="fa-solid fa-magnifying-glass app-search-bar__search-icon" aria-hidden />
          <input
            type="text"
            placeholder="Search apps..."
            value={searchTerm}
            onChange={(e) => onSearchChange?.(e.target.value)}
            className="app-search-bar__input"
          />
        </div>
        <button
          type="button"
          className="app-search-bar__settings-btn"
          onClick={handleMoreSettingsClick}
          aria-label="Filters and display settings"
          title="Filters and display settings"
          aria-expanded={settingsMenuOpen}
        >
          <i className="fa-solid fa-sliders" aria-hidden />
        </button>
      </div>
      {/* Row 2: Tag input + chips */}
      <div className="app-search-bar__row app-search-bar__row--tags">
        <input
          type="text"
          placeholder="Add tags"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={handleTagKeyDown}
          className="app-search-bar__tag-input"
        />
        <div className="app-search-bar__tags">
          {tags.map((tag) => (
            <button
              key={tag}
              type="button"
              className="app-search-bar__tag"
              onClick={() => handleRemoveTag(tag)}
              aria-label={`Remove ${tag} tag`}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>
      <RevoltPopup
        isOpen={settingsMenuOpen}
        onClose={() => setSettingsMenuOpen(false)}
        top={settingsAnchor.y}
        left={settingsAnchor.x}
        transform="translate(-100%, 0)"
        minWidth={280}
        panelClassName="app-search-bar__settings-popup"
      >
        <div className="app-search-bar__settings-popup-inner">
          <div className="app-search-bar__settings-field">
            <label htmlFor="app-sort-select" className="app-search-bar__settings-field-label">
              Sort by
            </label>
            <DropdownSelect
              id="app-sort-select"
              value={sortBy}
              options={SORT_OPTIONS}
              onChange={(e) => onSortChange?.(e.target.value)}
              className="app-search-bar__filter-select app-search-bar__settings-field-select"
              menuClassName="app-management-sort-menu"
            />
          </div>
          <div className="app-search-bar__settings-field">
            <label htmlFor="app-favorites-select" className="app-search-bar__settings-field-label">
              Favorites
            </label>
            <DropdownSelect
              id="app-favorites-select"
              value={favoritesFilter}
              options={FAVORITES_OPTIONS}
              onChange={(e) => onFavoritesChange?.(e.target.value)}
              className="app-search-bar__filter-select app-search-bar__settings-field-select"
              menuClassName="app-management-sort-menu"
            />
          </div>
          <div className="app-search-bar__settings-field">
            <label htmlFor="app-owned-select" className="app-search-bar__settings-field-label">
              Owned
            </label>
            <DropdownSelect
              id="app-owned-select"
              value={ownedFilter}
              options={OWNED_OPTIONS}
              onChange={(e) => onOwnedChange?.(e.target.value)}
              className="app-search-bar__filter-select app-search-bar__settings-field-select"
              menuClassName="app-management-sort-menu"
            />
          </div>
          {onPinFavoritesTopChange ? (
            <div className="app-search-bar__settings-row app-search-bar__settings-row--pin">
              <RevoltSwitch
                id="app-search-pin-favorites-top"
                checked={pinFavoritesTop}
                onChange={(next) => onPinFavoritesTopChange?.(!!next)}
                label="Pin favorites to top"
              />
            </div>
          ) : null}
        </div>
      </RevoltPopup>
    </div>
  );
}
